// ─── Helpers PURS du plan 2D des emplacements ────────────────────────────────
// Partagés entre l'écran Inventaire (éditeur) et la page « Plans & Emplacements ».
// (navigation/visualisation). Aucune dépendance UI : logique pure, testable.
//
//   noeud emplacement : { id, nom, emoji, enfants:[noeud], lieuId?,
//                         planCols?, planRows?, planMurs?, murV?, cell?, cells? }
//   cell  : { x, y, w, h }  (rectangle englobant)
//   cells : [{x,y}]         (zone « forme libre » non rectangulaire)
//   mur   : { o:'v'|'h', x, y, type:'fin'|'epais'|'porte'|'fenetre', ep? }

import { parentDe, aplatirArbre, noeudParId } from './inventaires';

// Liste des cases occupées par une zone : `cells` (forme libre) sinon le rectangle `cell`.
export function cellulesDe(node) {
  if (Array.isArray(node?.cells) && node.cells.length) return node.cells.map(c => ({ x: c.x, y: c.y }));
  const c = node?.cell;
  if (c && Number.isInteger(c.x)) {
    const w = Math.max(1, c.w || 1), h = Math.max(1, c.h || 1), out = [];
    for (let i = 0; i < w; i++) for (let j = 0; j < h; j++) out.push({ x: c.x + i, y: c.y + j });
    return out;
  }
  return null;   // pas encore placée (en flux)
}

// Plus grand rectangle plein contenu dans un ensemble de cases (pour y centrer le libellé).
export function plusGrandRect(cells) {
  if (!cells || !cells.length) return { x: 0, y: 0, w: 1, h: 1 };
  const has = new Set(cells.map(c => `${c.x},${c.y}`));
  const dans = (x, y) => has.has(`${x},${y}`);
  const xs = cells.map(c => c.x), ys = cells.map(c => c.y);
  const minx = Math.min(...xs), maxx = Math.max(...xs), miny = Math.min(...ys), maxy = Math.max(...ys);
  let best = { x: cells[0].x, y: cells[0].y, w: 1, h: 1 }, bestA = 1;
  for (let x0 = minx; x0 <= maxx; x0++) for (let y0 = miny; y0 <= maxy; y0++) {
    if (!dans(x0, y0)) continue;
    for (let x1 = x0; x1 <= maxx && dans(x1, y0); x1++) {
      for (let y1 = y0; y1 <= maxy; y1++) {
        let ok = true;
        for (let xx = x0; xx <= x1 && ok; xx++) for (let yy = y0; yy <= y1; yy++) if (!dans(xx, yy)) { ok = false; break; }
        if (!ok) break;
        const a = (x1 - x0 + 1) * (y1 - y0 + 1);
        if (a > bestA) { bestA = a; best = { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 }; }
      }
    }
  }
  return best;
}

// Murs en coordonnées « lignes de grille » (0..cols / 0..rows), rétro-compatible.
// Ancien schéma (murV absent) : 'v' bord droit de la case x → ligne x+1 ; 'h' bord bas de y → ligne y+1.
export function murEnGrille(node) {
  const murs = Array.isArray(node?.planMurs) ? node.planMurs : [];
  if ((node?.murV || 0) >= 2) return murs;
  return murs.map(m => m.o === 'v' ? { ...m, x: (m.x || 0) + 1 } : { ...m, y: (m.y || 0) + 1 });
}

// Dispose les enfants d'un emplacement sur une grille (cases placées d'abord, reste en flux).
// flux=true (visualisation) : les sous-lieux non positionnés sont auto-placés en flux.
// flux=false (édition) : ils NE sont PAS placés → renvoyés dans `nonPlaces` (liste « à placer »).
export function disposerPlan(parent, flux = true) {
  const cols = Math.max(1, parent?.planCols || 3);
  const enfants = parent?.enfants || [];
  const occ = {};
  const mark = (x, y) => { occ[`${x},${y}`] = true; };
  const place = [];
  const nonPlaces = [];
  for (const n of enfants) {
    const cl0 = cellulesDe(n);   // null si non placé (ni cell.x ni cells)
    if (cl0 && cl0.length) {
      const cl = cl0.map(c => ({ x: Math.max(0, Math.min(cols - 1, c.x)), y: Math.max(0, c.y) }));
      const xs = cl.map(c => c.x), ys = cl.map(c => c.y);
      const x = Math.min(...xs), y = Math.min(...ys);
      place.push({ node: n, x, y, w: Math.max(...xs) - x + 1, h: Math.max(...ys) - y + 1, cells: cl });
      cl.forEach(c => mark(c.x, c.y));
    } else {
      nonPlaces.push(n);
    }
  }
  const libre = () => { for (let y = 0; y < 50; y++) for (let x = 0; x < cols; x++) if (!occ[`${x},${y}`]) return { x, y }; return { x: 0, y: 0 }; };
  if (flux) {
    for (const n of nonPlaces) { const { x, y } = libre(); place.push({ node: n, x, y, w: 1, h: 1, cells: [{ x, y }] }); mark(x, y); }
    nonPlaces.length = 0;
  }
  const rows = Math.max(2, parent?.planRows || 0, ...place.flatMap(p => p.cells.map(c => c.y + 1)));
  return { cols, rows, place, nonPlaces };
}

// Chemin d'ids racine → id (inclus) dans un arbre d'emplacements.
export function cheminIdsEmp(arbre, id) {
  const out = [];
  let cur = id;
  const garde = new Set();
  while (cur && !garde.has(cur)) {
    garde.add(cur);
    out.unshift(cur);
    const pp = parentDe(arbre, cur);
    cur = pp && pp.parent ? pp.parent.id : null;
  }
  return out;
}

// ── Comptage d'objets par emplacement (agrégé multi-inventaires) ──────────────
// `articles` : liste plate d'articles (chacun avec emplacementId?). Renvoie une
// Map id → nombre d'articles rangés DIRECTEMENT dans cet emplacement.
export function compterDirect(articles) {
  const m = new Map();
  for (const a of (articles || [])) {
    const e = a && a.emplacementId;
    if (e) m.set(e, (m.get(e) || 0) + 1);
  }
  return m;
}

// Total (direct + tous les sous-emplacements) pour chaque noeud de l'arbre.
// Renvoie une Map id → total.
export function compterTotaux(arbre, directMap) {
  const tot = new Map();
  const marche = (n) => {
    let s = directMap.get(n.id) || 0;
    for (const e of (n.enfants || [])) s += marche(e);
    tot.set(n.id, s);
    return s;
  };
  (arbre || []).forEach(marche);
  return tot;
}

// ── Mutations PURES du plan (mutent l'arbre passé — le caller clone d'abord) ──
// Partagées entre l'éditeur d'Inventaire et la page « Plans & Emplacements ».

// Recalcule le rectangle englobant d'une zone + range en rectangle plein si possible.
function majFootprint(c, cells) {
  const xs = cells.map(k => k.x), ys = cells.map(k => k.y);
  const minx = Math.min(...xs), miny = Math.min(...ys);
  c.cell = { x: minx, y: miny, w: Math.max(...xs) - minx + 1, h: Math.max(...ys) - miny + 1 };
  if (cells.length === c.cell.w * c.cell.h) delete c.cells; else c.cells = cells;
}

// Nombre de colonnes de la grille (± bornes 1..6).
export function majPlanCols(tree, parentId, delta) {
  const n = noeudParId(tree, parentId);
  if (n) n.planCols = Math.max(1, Math.min(6, (n.planCols || 3) + delta));
}

// Nombre de lignes (± : jamais sous le contenu déjà placé, max 10).
export function majPlanRows(tree, parentId, delta) {
  const n = noeudParId(tree, parentId); if (!n) return;
  let contenu = 2;
  for (const c of (n.enfants || [])) if (c && c.cell && Number.isInteger(c.cell.x)) contenu = Math.max(contenu, (c.cell.y || 0) + (c.cell.h || 1));
  const base = n.planRows || contenu;
  n.planRows = Math.max(contenu, Math.min(10, base + delta));
}

// Pose / retire une séparation sur un bord (coordonnées lignes de grille).
// type : 'fin'|'epais'|'porte'|'fenetre'. Même type au même endroit → retire ; sinon → remplace.
// Une porte/fenêtre posée sur un mur épais conserve l'épaisseur (flag ep).
export function poserMur(tree, parentId, o, x, y, type) {
  const n = noeudParId(tree, parentId); if (!n) return;
  const murs = murEnGrille(n).slice();
  const faire = (base) => {
    const wasEpais = base && (base.type === 'epais' || base.ep === 'epais');
    if (type === 'porte' || type === 'fenetre') return { o, x, y, type, ...(wasEpais ? { ep: 'epais' } : {}) };
    return { o, x, y, type };
  };
  const i = murs.findIndex(m => m.o === o && m.x === x && m.y === y);
  if (i >= 0) {
    if ((murs[i].type || 'fin') === type) murs.splice(i, 1);
    else murs[i] = faire(murs[i]);
  } else murs.push(faire(null));
  n.planMurs = murs; n.murV = 2;
}

// Positionne / déplace une zone à partir de la case (x,y) (translation pour les formes libres).
export function placerPlan(tree, childId, x, y) {
  const c = noeudParId(tree, childId); if (!c) return;
  if (Array.isArray(c.cells) && c.cells.length) {
    const base = c.cell || { x: c.cells[0].x, y: c.cells[0].y };
    const dx = x - base.x, dy = y - base.y;
    majFootprint(c, c.cells.map(k => ({ x: k.x + dx, y: k.y + dy })));
  } else {
    c.cell = { ...(c.cell || { w: 1, h: 1 }), x, y, w: (c.cell?.w || 1), h: (c.cell?.h || 1) };
  }
}

// Redimensionne une zone rectangulaire (axe 'w' ou 'h', ± bornes 1..6).
export function redimPlan(tree, childId, axe, delta) {
  const c = noeudParId(tree, childId); if (!c) return;
  const cell = { x: 0, y: 0, w: 1, h: 1, ...(c.cell || {}) };
  cell[axe] = Math.max(1, Math.min(6, (cell[axe] || 1) + delta));
  c.cell = cell;
}

// Ajoute / retire une case à une zone (forme libre) : ajout = case libre ET adjacente ; retrait = ≥1 case.
export function basculerCellZone(tree, childId, x, y) {
  const c = noeudParId(tree, childId); if (!c) return;
  let cells = cellulesDe(c) || [{ x: 0, y: 0 }];
  const has = cells.some(k => k.x === x && k.y === y);
  if (has) {
    if (cells.length <= 1) return;
    cells = cells.filter(k => !(k.x === x && k.y === y));
  } else {
    const par = parentDe(tree, childId);
    const occ = new Set();
    for (const s of (par?.enfants || [])) if (s.id !== childId) (cellulesDe(s) || []).forEach(k => occ.add(`${k.x},${k.y}`));
    if (occ.has(`${x},${y}`)) return;
    if (!cells.some(k => Math.abs(k.x - x) + Math.abs(k.y - y) === 1)) return;
    cells = cells.concat([{ x, y }]);
  }
  majFootprint(c, cells);
}

// (aplatirArbre reste exporté par services/inventaires — pas de ré-export ici,
// sinon le `export *` de l'index rend le nom ambigu.)
